import React, { useCallback, useEffect, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Dropdown, Form, Row, Spinner, Table } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import Swal from 'sweetalert2';
import useStudyYearSelector from '../discipline/useStudyYearSelector';
import { todayISO } from './nurseOptions';
import ThaiDateInput from '../components/ThaiDateInput'

// ตรวจสุขภาพทั้งห้องในตารางเดียว แล้วบันทึกครั้งเดียว — พยาบาลเรียกนักเรียนเข้าแถว
// ทีละห้อง การให้กรอกทีละคนแล้วกดบันทึกทีละครั้งคือการเพิ่มงานโดยไม่ได้อะไรกลับมา
//
// น้ำหนัก/ส่วนสูงลง tb_studentbodymass ตารางเดิมของครูประจำชั้น ไม่ใช่ตารางของห้องพยาบาล
// และภาวะโภชนาการ (เกณฑ์ WHO) คำนวณที่ server ตอนอ่าน ไม่เก็บลงคอลัมน์

const HEARING = [
  { value: 'na', label: '—' },
  { value: 'normal', label: 'ปกติ' },
  { value: 'abnormal', label: 'ผิดปกติ' },
];

const DENTAL = [
  { value: 'na', label: '—' },
  { value: 'normal', label: 'ปกติ' },
  { value: 'caries', label: 'ฟันผุ' },
  { value: 'refer', label: 'ส่งต่อทันตกรรม' },
];

const RESULT = [
  { value: 'normal', label: 'ปกติ', variant: 'success' },
  { value: 'watch', label: 'เฝ้าระวัง', variant: 'warning' },
  { value: 'refer', label: 'ส่งต่อ', variant: 'danger' },
];

const nutritionBadge = (n) => {
  if (!n || !n.bmi_label) return <span className="text-secondary">—</span>;
  const variant = n.bmi_flag && n.bmi_flag !== 'normal' ? 'warning' : 'success';
  return <Badge bg={variant} className="fw-normal">{n.bmi} · {n.bmi_label}</Badge>;
};

const rowFrom = (s) => ({
  studentId: s.st_id,
  weight: s.stbm_weight ?? '',
  height: s.stbm_height ?? '',
  visionL: s.nc_vision_l ?? '',
  visionR: s.nc_vision_r ?? '',
  hearing: s.nc_hearing ?? 'na',
  dental: s.nc_dental ?? 'na',
  skin: s.nc_skin ?? '',
  lice: !!s.nc_lice,
  bloodType: s.nc_blood_type ?? '',
  result: s.nc_result ?? 'normal',
  note: s.nc_note ?? '',
});

export default function NurseCheckup() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector();

  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState('');
  const [round, setRound] = useState(1);
  const [date, setDate] = useState(todayISO());
  const [students, setStudents] = useState([]);
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`/api/nurse/rooms?yearId=${yearId}`)
      .then(res => setRooms(res.data || []))
      .catch(() => setRooms([]));
  }, [yearId]);

  const fetchRoom = useCallback(() => {
    if (!yearId || !roomId) { setStudents([]); return; }
    setLoading(true);
    const month = Number(date.slice(5, 7));
    axiosInstance.get(`/api/nurse/checkup?yearId=${yearId}&roomId=${roomId}&round=${round}&month=${month}`)
      .then(res => {
        const list = res.data || [];
        setStudents(list);
        setRows(Object.fromEntries(list.map(s => [s.st_id, rowFrom(s)])));
      })
      .catch(() => setStudents([]))
      .finally(() => setLoading(false));
  }, [yearId, roomId, round, date]);

  useEffect(() => { fetchRoom(); }, [fetchRoom]);

  const setCell = (stId, key, value) =>
    setRows(prev => ({ ...prev, [stId]: { ...prev[stId], [key]: value } }));

  const save = () => {
    const list = Object.values(rows);
    if (!list.length) return;
    setSaving(true);
    axiosInstance.post('/api/nurse/checkup', { yearId, round, date, rows: list })
      .then(({ data }) => {
        fetchRoom();
        Swal.fire({
          icon: 'success',
          title: 'บันทึกแล้ว',
          text: `บันทึกผลตรวจ ${data.saved} คน (น้ำหนัก/ส่วนสูงบันทึกให้ทุกคนที่กรอก)`,
          timer: 2200,
          showConfirmButton: false,
        });
      })
      .catch(err => Swal.fire({ icon: 'error', title: 'ไม่สามารถบันทึกได้', text: err?.response?.data?.message || '' }))
      .finally(() => setSaving(false));
  };

  const doneCount = students.filter(s => s.nc_id).length;

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ห้องพยาบาล</li>
              <li className="breadcrumb-item active">ตรวจสุขภาพประจำปี</li>
            </ol>
            <h4 className="main-title mb-0">ตรวจสุขภาพประจำปี</h4>
            <div className="fs-xs text-secondary">
              กรอกทั้งห้องแล้วกดบันทึกครั้งเดียว · น้ำหนัก/ส่วนสูงจะบันทึกลงข้อมูลเดียวกับที่ครูประจำชั้นกรอก
            </div>
          </div>
          <Dropdown className="mt-3 mt-md-0">
            <Dropdown.Toggle variant="primary" id="nurse-checkup-year">
              <i className="ri-calendar-line me-1"></i>{yearLabel || 'เลือกปีการศึกษา'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map(year => (
                <Dropdown.Item key={year.year_id} onClick={() => handleSelectYear(year.year_id)}>
                  {`ปีการศึกษา ${year.year_year}`}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <Card className="card-one mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={4}>
                <Form.Label className="fw-semibold fs-xs mb-1">ห้องเรียน</Form.Label>
                <Form.Select size="sm" value={roomId} onChange={e => setRoomId(e.target.value)}>
                  <option value="">— เลือกห้อง —</option>
                  {rooms.map(r => (
                    <option key={r.r_id} value={r.r_id}>
                      ม.{r.c_id}/{r.r_no} ({r.student_count} คน)
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="fw-semibold fs-xs mb-1">ครั้งที่</Form.Label>
                <Form.Select size="sm" value={round} onChange={e => setRound(Number(e.target.value))}>
                  <option value={1}>ครั้งที่ 1</option>
                  <option value={2}>ครั้งที่ 2</option>
                </Form.Select>
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="fw-semibold fs-xs mb-1">วันที่ตรวจ</Form.Label>
                <ThaiDateInput size="sm" value={date} onChange={v => setDate(v)} />
              </Col>
              <Col xs={12} md={3} className="d-grid">
                <Button onClick={save} disabled={saving || !students.length}>
                  {saving ? <Spinner size="sm" animation="border" /> : (
                    <><i className="ri-save-line me-1"></i>บันทึกทั้งห้อง</>
                  )}
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="card-one">
          <Card.Header>
            <Card.Title as="h6" className="mb-0">
              {students.length ? `นักเรียน ${students.length} คน · ตรวจแล้ว ${doneCount} คน` : 'รายชื่อนักเรียน'}
            </Card.Title>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : !students.length ? (
              <div className="text-center text-secondary py-5 fs-sm">เลือกห้องเรียนเพื่อเริ่มกรอก</div>
            ) : (
              <div className="table-responsive">
                <Table className="mb-0 fs-sm align-middle" style={{ minWidth: 1100 }}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 180 }}>นักเรียน</th>
                      <th style={{ width: 90 }}>น้ำหนัก</th>
                      <th style={{ width: 90 }}>ส่วนสูง</th>
                      <th style={{ width: 120 }}>ภาวะโภชนาการ</th>
                      <th style={{ width: 130 }}>สายตา ซ้าย/ขวา</th>
                      <th style={{ width: 110 }}>การได้ยิน</th>
                      <th style={{ width: 130 }}>ช่องปาก</th>
                      <th style={{ width: 130 }}>ผิวหนัง</th>
                      <th style={{ width: 70 }}>เหา</th>
                      <th style={{ width: 90 }}>หมู่เลือด</th>
                      <th style={{ width: 120 }}>ผลสรุป</th>
                      <th style={{ minWidth: 140 }}>หมายเหตุ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(s => {
                      const row = rows[s.st_id] || rowFrom(s);
                      return (
                        <tr key={s.st_id}>
                          <td>
                            <div className="fw-semibold">{s.st_name}</div>
                            <div className="fs-xs text-secondary">
                              {s.st_idnumber}
                              {s.nc_id && <Badge bg="light" text="dark" className="ms-1 fw-normal">ตรวจแล้ว</Badge>}
                            </div>
                            {(s.st_drug || s.st_syndrome) && (
                              <div className="fs-xs text-danger">
                                {s.st_syndrome ? `โรค: ${s.st_syndrome} ` : ''}
                                {s.st_drug ? `แพ้ยา: ${s.st_drug}` : ''}
                              </div>
                            )}
                          </td>
                          <td>
                            <Form.Control size="sm" type="number" step="0.1" min="0" inputMode="decimal"
                              value={row.weight} onChange={e => setCell(s.st_id, 'weight', e.target.value)} />
                          </td>
                          <td>
                            <Form.Control size="sm" type="number" step="0.1" min="0" inputMode="decimal"
                              value={row.height} onChange={e => setCell(s.st_id, 'height', e.target.value)} />
                          </td>
                          <td>{nutritionBadge(s.nutrition)}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <Form.Control size="sm" placeholder="ซ้าย" value={row.visionL}
                                onChange={e => setCell(s.st_id, 'visionL', e.target.value)} />
                              <Form.Control size="sm" placeholder="ขวา" value={row.visionR}
                                onChange={e => setCell(s.st_id, 'visionR', e.target.value)} />
                            </div>
                          </td>
                          <td>
                            <Form.Select size="sm" value={row.hearing}
                              onChange={e => setCell(s.st_id, 'hearing', e.target.value)}>
                              {HEARING.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Select size="sm" value={row.dental}
                              onChange={e => setCell(s.st_id, 'dental', e.target.value)}>
                              {DENTAL.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control size="sm" value={row.skin} maxLength={100}
                              onChange={e => setCell(s.st_id, 'skin', e.target.value)} />
                          </td>
                          <td className="text-center">
                            <Form.Check checked={row.lice} aria-label="พบเหา"
                              onChange={e => setCell(s.st_id, 'lice', e.target.checked)} />
                          </td>
                          <td>
                            <Form.Control size="sm" value={row.bloodType} maxLength={5}
                              onChange={e => setCell(s.st_id, 'bloodType', e.target.value)} />
                          </td>
                          <td>
                            <Form.Select size="sm" value={row.result}
                              onChange={e => setCell(s.st_id, 'result', e.target.value)}>
                              {RESULT.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control size="sm" value={row.note} maxLength={255}
                              onChange={e => setCell(s.st_id, 'note', e.target.value)} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
          {students.length > 0 && (
            <Card.Footer className="d-flex justify-content-end">
              <Button onClick={save} disabled={saving}>
                {saving ? <Spinner size="sm" animation="border" /> : (
                  <><i className="ri-save-line me-1"></i>บันทึกทั้งห้อง</>
                )}
              </Button>
            </Card.Footer>
          )}
        </Card>
        <Footer />
      </div>
    </>
  );
}
